import styled from "@emotion/styled";
import { useEffect, useState } from "react";
import { Container } from "@mui/system";
import { Button } from "@mui/material";
import { toast } from "react-toastify";
import { helpHttp } from "../../helper/helpHttp";

const COLORS = {
  azulOscuro: "#0e2b54",
  azulCard: "#0e6dba",
  azulCardHover: "#0a4f8a",
  amarillo: "#FBBF24",
  amarilloSoft: "#FDE68A",
  fondo: "#f6f7fb",
  superficie: "#ffffff",
  superficieDeshabilitada: "#eef0f4",
  borde: "#e5e7eb",
  textoSuave: "#475569",
  liveRojo: "#dc2626",
};

// Toggle del botón "Vive Alimentec en Vivo".
// Cambiar `enabled` a true cuando el evento esté en vivo y poner la URL del stream.
const VIVE_ALIMENTEC = {
  enabled: true,
  url: "https://us06web.zoom.us/j/8828043634?pwd=QU5mNlNUWXhtdGo0eE5CZk8wbys5dz09&omn=86199838611",
};

const extractVimeoId = (url) => {
  if (!url || typeof url !== "string") return null;
  const match = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return match ? match[1] : null;
};

export const Alimentec = () => {
  const { get, post } = helpHttp();
  const [catalogo, setCatalogo] = useState(null);
  const [invitados, setInvitados] = useState([]);
  const [demostraciones, setDemostraciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [descargandoId, setDescargandoId] = useState(null);

  useEffect(() => {
    post("demostraciones/clickDemostracion", {
      body: { id_demostracion: "1" },
    }).catch(() => {});

    get("demostraciones/getCatalogoDemostraciones/")
      .then((res) => {
        if (res?.codigo === "200" && res?.datos) {
          setCatalogo(res.datos.catalogo ?? null);
          setInvitados(res.datos.invitados ?? []);
          setDemostraciones(res.datos.demostraciones ?? []);
        } else {
          toast.error("No se pudo cargar el catálogo de demostraciones");
        }
      })
      .catch(() => {
        toast.error("Ha ocurrido un error al cargar las demostraciones");
      })
      .finally(() => setLoading(false));
  }, []);

  const handleDescargarReceta = async (demo) => {
    if (descargandoId) return;
    setDescargandoId(demo.id);
    try {
      const res = await post("demostraciones/getDocumentoDemostracion", {
        body: { id_demostracion: demo.id },
      });
      if (res?.codigo !== "200") {
        throw new Error(res?.mensaje ?? "No se pudo descargar la receta");
      }
      if (res?.datos?.habilitado !== "1") {
        throw new Error("La receta aún no está disponible");
      }
      const pdfBase64 = res?.datos?.pdf;
      if (!pdfBase64) {
        throw new Error("La receta no está disponible");
      }

      const href = pdfBase64.startsWith("data:")
        ? pdfBase64
        : `data:application/pdf;base64,${pdfBase64}`;

      const link = document.createElement("a");
      link.href = href;
      link.download = `receta-${demo.titulo ?? demo.id}.pdf`;
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      toast.error(err?.message ?? "No se pudo descargar la receta");
    } finally {
      setDescargandoId(null);
    }
  };

  const vimeoId = extractVimeoId(catalogo?.link_video);

  return (
    <StyledContainer>
      <Container maxWidth={false} sx={{ maxWidth: 1000 }}>
        <StyledMain>
          <StyledVideo>
            {vimeoId && (
              <iframe
                width="100%"
                height="100%"
                src={`https://player.vimeo.com/video/${vimeoId}`}
                frameBorder="0"
                allow="fullscreen; picture-in-picture"
                title="Video corporativo Alimentec"
                loading="lazy"
              />
            )}
          </StyledVideo>

          <StyledIntro vivoActivo={VIVE_ALIMENTEC.enabled}>
            <StyledDescripcion vivoActivo={VIVE_ALIMENTEC.enabled}>
              Disfruta de las demostraciones exclusivas de Fleischmann en
              alimentec
            </StyledDescripcion>
            {VIVE_ALIMENTEC.enabled && (
              <StyledLiveLink
                href={VIVE_ALIMENTEC.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span aria-hidden="true" />
                Vive Alimentec<br/> en Vivo
              </StyledLiveLink>
            )}
          </StyledIntro>

          <StyledSeccion>
            <StyledBadge>Invitados especiales</StyledBadge>
            {loading ? (
              <StyledLoading>Cargando invitados...</StyledLoading>
            ) : invitados.length === 0 ? (
              <StyledLoading>No hay invitados disponibles.</StyledLoading>
            ) : (
              <StyledInvitadosGrid>
                {invitados.map((inv) => (
                  <StyledInvitadoCard key={inv.id}>
                    <img src={inv.foto} alt={`Invitado ${inv.id}`} />
                  </StyledInvitadoCard>
                ))}
              </StyledInvitadosGrid>
            )}
          </StyledSeccion>

          <StyledSeccion>
            <StyledBadge>Demostraciones</StyledBadge>
            {loading ? (
              <StyledLoading>Cargando demostraciones...</StyledLoading>
            ) : demostraciones.length === 0 ? (
              <StyledLoading>No hay demostraciones disponibles.</StyledLoading>
            ) : (
              <StyledDemosList>
                {demostraciones.map((demo) => {
                  const deshabilitada = !demo.video;
                  return (
                  <StyledDemoCard key={demo.id} deshabilitada={deshabilitada}>
                    <StyledDemoPortada>
                      <img src={demo.portada} alt={demo.titulo} />
                    </StyledDemoPortada>
                    <StyledDemoInfo>
                      <StyledDemoLabel>Descripción</StyledDemoLabel>
                      <StyledDemoTitle>{demo.titulo}</StyledDemoTitle>
                      <StyledDemoText>{demo.descripcion}</StyledDemoText>
                      <Button
                        variant="contained"
                        disabled={deshabilitada || descargandoId === demo.id}
                        onClick={() => handleDescargarReceta(demo)}
                        sx={{
                          alignSelf: "flex-start",
                          backgroundColor: COLORS.azulCard,
                          borderRadius: "999px",
                          textTransform: "none",
                          fontWeight: 600,
                          fontSize: "0.92rem",
                          paddingX: 3.2,
                          paddingY: 1,
                          boxShadow: "0 6px 16px rgba(14,109,186,0.25)",
                          transition: "transform .2s ease, box-shadow .2s ease",
                          "&:hover": {
                            backgroundColor: COLORS.azulCardHover,
                            transform: "translateY(-1px)",
                            boxShadow: "0 8px 22px rgba(14,109,186,0.32)",
                          },
                          "&.Mui-disabled": {
                            backgroundColor: COLORS.azulCard,
                            color: "#fff",
                            opacity: 0.5,
                            boxShadow: "none",
                          },
                        }}
                      >
                        {descargandoId === demo.id
                          ? "Descargando..."
                          : "Descargar receta"}
                      </Button>
                    </StyledDemoInfo>
                  </StyledDemoCard>
                  );
                })}
              </StyledDemosList>
            )}
          </StyledSeccion>
        </StyledMain>
      </Container>
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  background: linear-gradient(180deg, ${COLORS.fondo} 0%, #eef1f7 100%);
  min-height: calc(100vh - 64px);
`;

const StyledMain = styled.div`
  padding-top: 120px;
  padding-bottom: 80px;
  display: flex;
  flex-direction: column;
  gap: 64px;
`;

const StyledVideo = styled.div`
  width: 100%;
  aspect-ratio: 16/9;
  background: #000;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 18px 40px -16px rgba(14, 43, 84, 0.35);

  iframe {
    border: 0;
  }
`;

const StyledIntro = styled.div`
  display: flex;
  flex-direction: ${({ vivoActivo }) => (vivoActivo ? "row" : "column")};
  align-items: center;
  justify-content: ${({ vivoActivo }) =>
    vivoActivo ? "space-between" : "center"};
  gap: 24px;
  text-align: ${({ vivoActivo }) => (vivoActivo ? "left" : "center")};
  margin-top: -16px;

  @media (max-width: 720px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

const StyledKicker = styled.span`
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${COLORS.azulCard};
  background: ${COLORS.amarilloSoft};
  padding: 6px 14px;
  border-radius: 999px;
`;

const StyledDescripcion = styled.h1`
  color: ${COLORS.azulOscuro};
  font-weight: 800;
  font-size: 2rem;
  line-height: 1.2;
  letter-spacing: -0.01em;
  margin: 0;
  max-width: 720px;
  flex: ${({ vivoActivo }) => (vivoActivo ? "1 1 auto" : "0 1 auto")};

  @media (max-width: 720px) {
    font-size: 1.5rem;
  }
`;

const StyledLiveLink = styled.a`
  height: 70px;
  width: 300px;
  justify-content: center;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: ${COLORS.amarillo};
  color: ${COLORS.azulOscuro};
  font-weight: bold;
  font-size: 1.2rem;
  text-decoration: none;
  text-align: center;
  padding: 8px 26px;
  border-radius: 999px;
  white-space: nowrap;
  box-shadow: 0 6px 16px rgba(14, 109, 186, 0.25);
  transition: transform 0.2s ease, box-shadow 0.2s ease,
    background-color 0.2s ease;

  span {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${COLORS.liveRojo};
    box-shadow: 0 0 0 4px rgba(220, 38, 38, 0.25);
    animation: pulseLive 1.4s ease-in-out infinite;
  }

  @keyframes pulseLive {
    0%, 100% {
      opacity: 1;
      box-shadow: 0 0 0 4px rgba(220, 38, 38, 0.25);
    }
    50% {
      opacity: 0.55;
      box-shadow: 0 0 0 8px rgba(220, 38, 38, 0);
    }
  }

  &:hover {
    background: ${COLORS.amarillo};
    transform: translateY(-1px);
    box-shadow: 0 8px 22px rgba(14, 109, 186, 0.32);
  }
`;

const StyledSeccion = styled.section`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const StyledBadge = styled.span`
  align-self: flex-start;
  background: ${COLORS.amarillo};
  color: ${COLORS.azulOscuro};
  font-weight: 700;
  letter-spacing: 0.04em;
  padding: 9px 22px;
  border-radius: 999px;
  font-size: 0.92rem;
  text-transform: uppercase;
  box-shadow: 0 6px 16px -8px rgba(251, 191, 36, 0.6);
`;

const StyledLoading = styled.p`
  margin: 0;
  color: ${COLORS.textoSuave};
  font-size: 0.95rem;
`;

const StyledInvitadosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 18px;

  @media (max-width: 720px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StyledInvitadoCard = styled.div`
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 14px -6px rgba(14, 43, 84, 0.25);
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px -10px rgba(14, 43, 84, 0.4);
  }

  img {
    width: 100%;
    height: auto;
    display: block;
  }
`;

const StyledDemosList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 22px;
`;

const StyledDemoCard = styled.article`
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 28px;
  align-items: center;
  background: ${({ deshabilitada }) =>
    deshabilitada ? COLORS.superficieDeshabilitada : COLORS.superficie};
  border: 1px solid ${COLORS.borde};
  border-radius: 16px;
  padding: 20px;
  box-shadow: ${({ deshabilitada }) =>
    deshabilitada ? "none" : "0 4px 18px -10px rgba(14, 43, 84, 0.2)"};
  opacity: ${({ deshabilitada }) => (deshabilitada ? 0.6 : 1)};
  filter: ${({ deshabilitada }) =>
    deshabilitada ? "grayscale(70%)" : "none"};
  transition: transform 0.25s ease, box-shadow 0.25s ease,
    border-color 0.25s ease;

  &:hover {
    transform: ${({ deshabilitada }) =>
      deshabilitada ? "none" : "translateY(-2px)"};
    border-color: ${({ deshabilitada }) =>
      deshabilitada ? COLORS.borde : "#d6dbe4"};
    box-shadow: ${({ deshabilitada }) =>
      deshabilitada ? "none" : "0 14px 30px -14px rgba(14, 43, 84, 0.3)"};
  }

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    gap: 18px;
    padding: 16px;
  }
`;

const StyledDemoPortada = styled.div`
  background: ${COLORS.azulCard};
  aspect-ratio: 16/10;
  border-radius: 10px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const StyledDemoInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const StyledDemoLabel = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${COLORS.azulCard};
`;

const StyledDemoTitle = styled.h3`
  margin: 0;
  color: ${COLORS.azulOscuro};
  font-size: 1.2rem;
  font-weight: 700;
  line-height: 1.3;
`;

const StyledDemoText = styled.p`
  margin: 0 0 8px;
  color: ${COLORS.textoSuave};
  font-size: 0.95rem;
  line-height: 1.55;
`;
